/**
 * Pure model for /admin/access-requests - no React, so it is unit-testable.
 *
 * The queue is fully server-driven: the status tabs, the tier and source
 * filters, the search box and the paging all run in `admin_list_access_requests`
 * (migration 544). This module is the single place the URL search params
 * (`AccessRequestsSearch`) are parsed, normalised and turned into the API query,
 * plus the empty-state model, the derived pay link and the failure mapping the
 * review forms render.
 *
 * Two rules run through it:
 *  - The URL is the source of truth. `status=pending` is the default and is
 *    omitted from the URL; every other value is carried explicitly, so
 *    `?status=all&tier=K2K` is a link somebody can send.
 *  - An empty result must name the filter that emptied it. "The queue is clear"
 *    is only true when nothing is filtered - on a review queue an empty state
 *    that ignores its filters reads as "nobody is waiting" while people wait.
 */
import {
  type AccessRequestCounts,
  type AccessRequestSource,
  type AdminAccessRequestRow,
  type ListAccessRequestsParams,
} from '@/api/admin-access';
import {
  ACCESS_REQUEST_TIER_CODES,
  ACCESS_REQUEST_TIER_LABEL,
  APPLICATION_TIER_CODES,
} from '@/api/access-requests';

// ---- tabs --------------------------------------------------------------------

/**
 * The four status tabs.
 *
 * `withdrawn` is deliberately NOT here. The value is reserved in the schema but
 * nothing in the product writes it - there is no applicant-withdraw path - so
 * the tab could only ever return nothing. If a withdraw path is ever built, the
 * tab comes back with it (design R6b).
 */
export type AccessRequestTab = 'pending' | 'approved' | 'rejected' | 'all';

export const ACCESS_REQUEST_TABS: readonly AccessRequestTab[] = [
  'pending',
  'approved',
  'rejected',
  'all',
];

export const ACCESS_REQUEST_TAB_LABELS: Record<AccessRequestTab, string> = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  all: 'All',
};

/** Count-badge tone per tab. Colour on the tab row means "this is a queue". */
export const ACCESS_REQUEST_TAB_TONE: Record<AccessRequestTab, 'neutral' | 'warning'> = {
  pending: 'warning',
  approved: 'neutral',
  rejected: 'neutral',
  all: 'neutral',
};

/** Pending is the queue, so it is the default view and is omitted from the URL. */
export const DEFAULT_ACCESS_REQUEST_TAB: AccessRequestTab = 'pending';

/** Fixed page size (design R6c). The RPC clamps to 1..200; the queue asks for 25. */
export const ACCESS_REQUESTS_PAGE_SIZE = 25;

export const ACCESS_REQUEST_SOURCES: readonly AccessRequestSource[] = ['alfie', 'ghl'];

export const ACCESS_REQUEST_SOURCE_LABELS: Record<AccessRequestSource, string> = {
  alfie: 'Alfie',
  ghl: 'GHL',
};

// ---- the URL contract --------------------------------------------------------

/** URL-persisted state. Every field is optional; a default is omitted. */
export type AccessRequestsSearch = {
  /** Omitted for the default tab (`pending`). */
  status?: Exclude<AccessRequestTab, 'pending'>;
  /** A single application-gated tier code. Omitted for "all tiers". */
  tier?: string;
  /** Omitted for "any source". */
  source?: AccessRequestSource;
  /** Server-side contains over email / full name / business name. */
  q?: string;
  /** Omitted for page 1. */
  page?: number;
};

function isTab(v: unknown): v is AccessRequestTab {
  return typeof v === 'string' && (ACCESS_REQUEST_TABS as readonly string[]).includes(v);
}

function isTier(v: unknown): v is string {
  return typeof v === 'string' && (ACCESS_REQUEST_TIER_CODES as readonly string[]).includes(v);
}

function isSource(v: unknown): v is AccessRequestSource {
  return typeof v === 'string' && (ACCESS_REQUEST_SOURCES as readonly string[]).includes(v);
}

function toInt(v: unknown): number | null {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
  return Number.isFinite(n) ? Math.floor(n) : null;
}

/**
 * Parse raw URL search into a canonical `AccessRequestsSearch`. Unknown values
 * are dropped and every default is normalised away, so the parsed object never
 * carries a redundant param (`status=pending`, `page=1`, …).
 *
 * `withdrawn` in an old bookmark resolves to `all` rather than 404-ing the tab
 * set: the value has no writer, so "everything" is the honest answer.
 */
export function parseAccessRequestsSearch(
  search: Record<string, unknown>,
): AccessRequestsSearch {
  const out: AccessRequestsSearch = {};

  const status = search.status === 'withdrawn' ? 'all' : search.status;
  if (isTab(status) && status !== DEFAULT_ACCESS_REQUEST_TAB) {
    out.status = status as Exclude<AccessRequestTab, 'pending'>;
  }

  // `tier_code` is the server's name for it; accept both so a hand-built link works.
  const tier = search.tier ?? search.tier_code;
  if (isTier(tier)) out.tier = tier;

  if (isSource(search.source)) out.source = search.source;

  if (typeof search.q === 'string' && search.q.trim() !== '') out.q = search.q;

  const page = toInt(search.page);
  if (page != null && page > 1) out.page = page;

  return out;
}

/**
 * Normalise a (possibly patched) search into the minimal object the URL should
 * carry: empty strings and every default are dropped, so navigate writes the
 * clean path.
 */
export function serializeAccessRequestsSearch(
  s: AccessRequestsSearch,
): AccessRequestsSearch {
  const out: AccessRequestsSearch = {};
  if (isTab(s.status) && s.status !== DEFAULT_ACCESS_REQUEST_TAB) out.status = s.status;
  if (isTier(s.tier)) out.tier = s.tier;
  if (isSource(s.source)) out.source = s.source;
  if (s.q && s.q.trim() !== '') out.q = s.q;
  if (s.page && s.page > 1) out.page = s.page;
  return out;
}

export const activeAccessRequestTab = (s: AccessRequestsSearch): AccessRequestTab =>
  s.status ?? DEFAULT_ACCESS_REQUEST_TAB;

export const accessRequestsPage = (s: AccessRequestsSearch): number =>
  s.page && s.page > 1 ? s.page : 1;

/** True when only the status tab is set - the one case "nothing waiting" is true. */
export function isUnfilteredQueue(s: AccessRequestsSearch): boolean {
  return !s.tier && !s.source && (!s.q || s.q.trim() === '');
}

/** Map the URL search to the `list_access_requests` API query. */
export function toAccessRequestsQuery(s: AccessRequestsSearch): ListAccessRequestsParams {
  return {
    // 'all' is the server's literal for "no status filter"; it is never omitted,
    // because omitting it would mean "every status" on a screen whose default
    // view is the pending queue.
    status: activeAccessRequestTab(s),
    tierCode: s.tier,
    source: s.source,
    search: s.q?.trim() || undefined,
    offset: (accessRequestsPage(s) - 1) * ACCESS_REQUESTS_PAGE_SIZE,
    limit: ACCESS_REQUESTS_PAGE_SIZE,
  };
}

// ---- counts ------------------------------------------------------------------

/**
 * A tab's count. `counts.status` is cross-filtered and IGNORES the status
 * filter - the parameter the tab writes - so every tab always says how many
 * rows it would reveal. An empty group is absent, not zero (backend §6).
 */
export function tabCount(
  counts: AccessRequestCounts | undefined,
  tab: AccessRequestTab,
): number | null {
  if (!counts) return null;
  if (tab === 'all') return Object.values(counts.status).reduce((sum, n) => sum + n, 0);
  return counts.status[tab] ?? 0;
}

/** Sum of a cross-filtered count group - the "all" option's own count. */
export function groupTotal(group: Record<string, number> | undefined): number | null {
  if (!group) return null;
  return Object.values(group).reduce((sum, n) => sum + n, 0);
}

/** Human label for an application tier code, falling back to the code. */
export function tierLabel(code: string, fallbackName?: string | null): string {
  return ACCESS_REQUEST_TIER_LABEL[code] ?? fallbackName ?? code;
}

// ---- the pay link ------------------------------------------------------------

/**
 * The link an approved applicant follows to pay, rebuilt from the request.
 *
 * This mirrors the edge function exactly: an applicant with an Alfie account
 * gets `/checkout?plan=`, one without gets `/signup?plan=&email=`. Deriving it
 * on the row is what makes Copy pay link work forever on the Approved tab - the
 * link used to exist only inside the panel that appeared at the moment of
 * approval, and a filter change or a reload destroyed it (audit D5/D6).
 *
 * `origin` is passed in rather than read from `window` so the function stays
 * pure. It is the browser's own origin, while the server builds its copy from
 * `SITE_URL`; in every deployed environment those are the same host. They can
 * diverge in local dev (a dev server on a different port), which is why a row
 * approved in the current session copies the server's `pay_url` verbatim and
 * only falls back to this.
 */
export function payLinkFor(
  row: Pick<AdminAccessRequestRow, 'tier_code' | 'email' | 'user_id'>,
  origin: string,
): string {
  const base = origin.replace(/\/+$/, '');
  return row.user_id
    ? `${base}/checkout?plan=${row.tier_code}`
    : `${base}/signup?plan=${row.tier_code}&email=${encodeURIComponent(row.email.toLowerCase())}`;
}

/** Whether the applicant has an Alfie account - decides which link they get. */
export const hasAlfieAccount = (row: Pick<AdminAccessRequestRow, 'user_id'>): boolean =>
  row.user_id != null;

// ---- how long the queue has waited ------------------------------------------

/** Whole days between an ISO timestamp and now. Negative clamps to 0. */
export function daysWaiting(iso: string, now: number = Date.now()): number {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return 0;
  return Math.max(0, Math.floor((now - t) / 86_400_000));
}

/**
 * The footer's one aggregate that changes behaviour (design R6a callout 4).
 *
 * Order is fixed newest-first, so the LAST row on the page is the oldest one on
 * it. That is the oldest waiting overall only when this is the last page, so the
 * wording says which - a number labelled "oldest waiting" that is really "oldest
 * on page 1 of 6" would be worse than no number.
 */
export function oldestWaitingLabel(
  rows: Pick<AdminAccessRequestRow, 'created_at'>[],
  opts: { isLastPage: boolean; now?: number },
): string | null {
  const last = rows[rows.length - 1];
  if (!last) return null;
  const days = daysWaiting(last.created_at, opts.now);
  const amount = days === 0 ? 'less than a day' : days === 1 ? '1 day' : `${days} days`;
  return opts.isLastPage ? `Oldest waiting: ${amount}.` : `Oldest on this page: ${amount}.`;
}

// ---- empty states ------------------------------------------------------------

export type EmptyAction =
  | { kind: 'clear_tier' }
  | { kind: 'clear_source' }
  | { kind: 'clear_search' }
  | { kind: 'go_status'; status: AccessRequestTab; label: string };

export interface EmptyStateModel {
  title: string;
  /** Plain sentences. The second one, when present, names what is hidden. */
  body: string;
  actions: EmptyAction[];
  /** True only when nothing is filtered - the one case that draws the tick. */
  clear: boolean;
}

const STATUS_NOUN: Record<AccessRequestTab, string> = {
  pending: 'pending',
  approved: 'approved',
  rejected: 'rejected',
  all: '',
};

function plural(n: number, one: string, many: string): string {
  return n === 1 ? `1 ${one}` : `${n} ${many}`;
}

/**
 * The empty state for a filter combination. Every variant that is not genuinely
 * empty names the filter that emptied it, states the count it is hiding, and
 * offers the one control that un-empties it (design R6d).
 */
export function emptyStateFor(
  search: AccessRequestsSearch,
  counts: AccessRequestCounts | undefined,
): EmptyStateModel {
  const tab = activeAccessRequestTab(search);
  const noun = STATUS_NOUN[tab];
  const q = search.q?.trim() ?? '';

  // 1. A search that matched nothing in this tab. The search is the loudest
  //    filter, so it is named first even when a tier or source is also set.
  if (q !== '') {
    const elsewhere = tab === 'pending' ? 0 : (counts?.status.pending ?? 0);
    const actions: EmptyAction[] = [];
    if (elsewhere > 0) {
      actions.push({ kind: 'go_status', status: 'pending', label: 'Search Pending instead' });
    }
    actions.push({ kind: 'clear_search' });
    return {
      title: tab === 'all'
        ? `No requests match “${q}”`
        : `No ${noun} requests match “${q}”`,
      body: elsewhere > 0
        ? `There ${elsewhere === 1 ? 'is' : 'are'} ${plural(elsewhere, 'pending request', 'pending requests')} matching “${q}”.`
        : 'Nothing on any tab matches this search.',
      actions,
      clear: false,
    };
  }

  // 2. A tier filter with rows on the other side of it. counts.tier ignores the
  //    tier filter and applies the rest, so the difference IS what is hidden.
  if (search.tier) {
    const total = groupTotal(counts?.tier) ?? 0;
    const hidden = Math.max(0, total - (counts?.tier[search.tier] ?? 0));
    const others = Object.entries(counts?.tier ?? {})
      .filter(([code, n]) => code !== search.tier && n > 0)
      .map(([code]) => tierLabel(code));
    return {
      title: `No ${noun ? `${noun} ` : ''}${tierLabel(search.tier)} applications`.trim(),
      body: hidden > 0
        ? `${plural(hidden, 'application is', 'applications are')} hidden by this filter${
          others.length > 0 ? `, for ${others.join(' and ')}` : ''
        }.`
        : `Nothing is hidden by this filter - there are no ${noun || 'access'} applications at all.`,
      actions: hidden > 0 ? [{ kind: 'clear_tier' }] : [],
      clear: false,
    };
  }

  // 3. A source filter with rows on the other side of it.
  if (search.source) {
    const total = groupTotal(counts?.source) ?? 0;
    const hidden = Math.max(0, total - (counts?.source[search.source] ?? 0));
    return {
      title: `No ${noun ? `${noun} ` : ''}applications from ${
        ACCESS_REQUEST_SOURCE_LABELS[search.source]
      }`.trim(),
      body: hidden > 0
        ? `${plural(hidden, 'application is', 'applications are')} hidden by this filter.`
        : `Nothing is hidden by this filter - there are no ${noun || 'access'} applications at all.`,
      actions: hidden > 0 ? [{ kind: 'clear_source' }] : [],
      clear: false,
    };
  }

  // 4. Genuinely empty. Only here may the copy say the queue is clear.
  if (tab === 'pending') {
    const approved = counts?.status.approved ?? 0;
    return {
      title: 'Nothing waiting',
      body: `No pending applications for ${APPLICATION_TIER_CODES.map((c) => tierLabel(c)).join(
        ' or ',
      )}.`,
      actions: approved > 0
        ? [{
          kind: 'go_status',
          status: 'approved',
          label: `See the ${approved.toLocaleString()} approved ${
            approved === 1 ? 'request' : 'requests'
          }`,
        }]
        : [],
      clear: true,
    };
  }

  return {
    title: tab === 'all' ? 'No access requests yet' : `No ${noun} requests yet`,
    body: tab === 'all'
      ? 'Applications appear here when someone applies for K2K or Inner Circle, in the app or through a GHL funnel, and Enterprise inquiries when someone asks about Enterprise.'
      : `Nothing has been ${noun} yet.`,
    actions: [],
    clear: true,
  };
}

// ---- review failures ---------------------------------------------------------

/**
 * The four things a decision can hit, in the reviewer's language.
 *
 * The old mapping was dead code: it switched on codes like `not_pending` while
 * the edge function sends human sentences ('Access request is not pending'), so
 * nothing ever matched and the raw server text was rendered (audit D1). These
 * match what the function ACTUALLY sends, and every one of them says what state
 * things are in now - after an error the honest question is not "what broke" but
 * "did it half-happen".
 */
export type ReviewFailureKind = 'reason' | 'decided' | 'permission' | 'gone' | 'failed';

export interface ReviewFailure {
  kind: ReviewFailureKind;
  tone: 'warn' | 'crit';
  title: string;
  body: string;
  /** The raw server string, kept for a bug report - never the headline. */
  technical: string | null;
  /** True when the list should be reloaded to show the settled state. */
  offerRefresh: boolean;
}

export function reviewFailure(
  error: unknown,
  ctx: { decision: 'approve' | 'reject' | 'resend'; applicant: string },
): ReviewFailure {
  const raw = error instanceof Error ? error.message.trim() : '';
  const lower = raw.toLowerCase();
  const who = ctx.applicant || 'The applicant';

  if (lower.includes('reason_required')) {
    return {
      kind: 'reason',
      tone: 'warn',
      title: 'The reason is too short',
      body:
        'Three characters minimum. This one is read months later by someone who was not on the call.',
      technical: null,
      offerRefresh: false,
    };
  }

  if (lower.includes('not pending') || lower.includes('not approved')) {
    return {
      kind: 'decided',
      tone: 'warn',
      title: 'Already decided',
      body: `Someone else decided this application first. Nothing was changed - reload to see where ${who} stands now.`,
      technical: raw || null,
      offerRefresh: true,
    };
  }

  if (lower.includes('not found')) {
    return {
      kind: 'gone',
      tone: 'warn',
      title: 'This application no longer exists',
      body: 'Nothing was changed. Reload the queue.',
      technical: raw || null,
      offerRefresh: true,
    };
  }

  if (lower.includes('admin access required') || lower.includes('unauthorized')) {
    return {
      kind: 'permission',
      tone: 'crit',
      title: 'You do not have permission to decide access requests',
      body: 'Deciding needs super admin or the users/admin permission. Ask a super admin.',
      technical: raw || null,
      offerRefresh: false,
    };
  }

  const what = ctx.decision === 'approve'
    ? 'The approval did not go through'
    : ctx.decision === 'reject'
      ? 'The rejection did not go through'
      : 'The email was not resent';
  const state = ctx.decision === 'approve'
    ? `Nothing was written and no email was sent. ${who} is still pending. Try again.`
    : ctx.decision === 'reject'
      ? `Nothing was written. ${who} is still pending. Try again.`
      : 'The approval itself is untouched. Try again, or copy the pay link and send it by hand.';
  return {
    kind: 'failed',
    tone: 'crit',
    title: what,
    body: state,
    technical: raw || null,
    offerRefresh: false,
  };
}
